import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Fab from '@mui/material/Fab';
import Divider from '@mui/material/Divider';
import { useTheme } from "@mui/material/styles";
import MenuCategories from "./components/MenuCategories";
import MenuItems from "./components/MenuItems";
import Cart from "./components/Cart";
import TableSelect from "./components/TableSelect";
import DiningLocation from "./components/DiningLocation";
import TableNumber from "./components/TableNumber";
import DeliverySelect from "./components/DeliverySelect";
import CartReview from "./components/CartReview";
import CashPayment from "./components/CashPayment";
import OrderNumber from "./components/OrderNumber";
import PaymentDialog from "./components/PaymentDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LanguageIcon from "@mui/icons-material/Language";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { KioskProvider, useKioskContext, LANGUAGES } from "./context/KioskContext";

// Wrap entire KioskPage in the KioskProvider
const KioskPageWrapper = () => {
  return (
    <KioskProvider>
      <KioskPageContent />
    </KioskProvider>
  );
};

// Main kiosk page content with context
const KioskPageContent = () => {
  const { posId } = useParams();
  var id = posId;

  useEffect(() => {
    document.title = "Cashier";
  }, []);

  if(posId === "test") {
    id ="672312ec5b53fbbe37d4b0d5"
  } else if (posId === "khiang") {
    id="67d3a2144a266ea37ce7ac69"
  } else if (posId === "khiangmpob") {
    id="680e35b78a5a51ec452b6678"
  }
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { 
    darkMode, 
    toggleDarkMode, 
    language, 
    changeLanguage, 
    availableLanguages,
    translate,
    paymentMethod,
    changePaymentMethod,
    formatPrice,
    orderType,
    changeOrderType,
    clearCart,
    setSelectedDelivery: updateSelectedDelivery,
  } = useKioskContext();
  
  // UI flow stages
  const STAGES = {
    DINING_LOCATION: 'dining_location',
    DELIVERY_SELECT: 'delivery_select',
    TABLE_NUMBER: 'table_number',
    ORDER_NUMBER: 'order_number',
    ORDER_MENU: 'order_menu',
    CART_REVIEW: 'cart_review',
    CASH_PAYMENT: 'cash_payment',
    PAYMENT: 'payment',
  };
  
  // Current stage of the ordering flow
  const [currentStage, setCurrentStage] = useState(STAGES.DINING_LOCATION);
  
  const [menuData, setMenuData] = useState({
    availableMenu: [],
    outOfStockMenu: [],
  });
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [tableNumber, setTableNumber] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [orderReceiptNumber, setOrderReceiptNumber] = useState(null);
  const [orderOrderNumber, setOrderOrderNumber] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loadedOrderData, setLoadedOrderData] = useState(null);
  const [discountDetails, setDiscountDetails] = useState({
    discount: 0,
    discountType: "None",
    discountValue: "",
    voucherCode: "",
    voucherDesc: "",
    finalTotal: 0,
  });
  const [discountDetails2, setDiscountDetails2] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({ customerId: null, orderId: null });
  const [categories, setCategories] = useState([]);
  const [cashPaymentDetails, setCashPaymentDetails] = useState(null);
  const [cartReviewVisible, setCartReviewVisible] = useState(false);

  // Settings menu state
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);
  const [paymentMethodMenuAnchorEl, setPaymentMethodMenuAnchorEl] = useState(null);
  
  // Fetch menu data from backend
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        setLoading(true);
        let response;
  
        if (orderType === "Delivery" && selectedDelivery) {
          response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-avail-delivery-menu?posId=${id}&deliveryName=${encodeURIComponent(selectedDelivery)}`
          );
        } else if (orderType === "Dine-In" || orderType === "Take-Out") {
          response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetchmenu?posId=${id}`
          );
        } else {
          setLoading(false);
          return; // Don't fetch if orderType not selected yet
        }
  
        setMenuData(response.data);
  
        const uniqueCategories = [...new Set(
          [...response.data.availableMenu, ...(response.data.outOfStockMenu || [])]
            .map(item => item.categoryName)
        )];
  
        setCategories(uniqueCategories);
        if (uniqueCategories.length > 0) {
          setSelectedCategory(uniqueCategories[0]);
        }
  
        setLoading(false);
      } catch (err) {
        console.error("Error fetching menu data:", err);
        setError("Failed to load menu data. Please try again later.");
        setLoading(false);
      }
    };
  
    fetchMenuData();
  }, [orderType, selectedDelivery, posId]);

  // Handle dining location selection
  const handleDiningLocationSelect = (locationType) => {
    if (locationType === 'Take-Out') {
      changeOrderType(locationType);
    setCurrentStage(STAGES.TABLE_NUMBER);
    } else if (locationType === 'Dine-In') {
      changeOrderType(locationType);
    setCurrentStage(STAGES.TABLE_NUMBER);
    } else if (locationType === 'Delivery') {
      changeOrderType(locationType);
      setCurrentStage(STAGES.DELIVERY_SELECT); 
    } else {
      setCurrentStage(STAGES.ORDER_NUMBER);
    }
  };
  // Handle table number submission
  const handleTableNumberSubmit = (tableNum) => {
    setTableNumber(tableNum);
    setCurrentStage(STAGES.ORDER_MENU);
  };
  
  // Handle navigation back to dining location from table number
  const handleBackToDiningLocation = () => {
    setCurrentStage(STAGES.DINING_LOCATION);
  };

  // Handle navigation back to table number from order menu
  const handleBackToTableNumber = () => {
    setCurrentStage(STAGES.TABLE_NUMBER);
  };
  
  // Handle starting over the ordering process
  const handleStartOver = () => {
    setCartItems([]);
    setTableNumber("");
    setDiscountDetails({
      discount: 0,
      discountType: "None",
      discountValue: "",
      voucherCode: "",
      voucherDesc: "",
      finalTotal: 0,
    });
    setDiscountDetails2([]);
    setCurrentStage(STAGES.DINING_LOCATION);
  };
  
  // Handle viewing cart review
  const handleViewCart = () => {
    setCurrentStage(STAGES.CART_REVIEW);
  };
  
  // Handle back to menu from cart review
  const handleBackToMenu = () => {
    setCurrentStage(STAGES.ORDER_MENU);
  };
  
  // Handle proceeding to payment from cart review
  const handleProceedToPayment = () => {
    if (paymentMethod === 'cash') {
      setCurrentStage(STAGES.CASH_PAYMENT);
    } else {
      handleCheckout();
    }
  };
  
  // Handle cash payment details submission
  const handleCashPaymentSubmit = (details) => {

    //receipt printing was done on previous screen - cash payment js with receipt dialog
    // alert('Cash payment details submitted - handleCashPaymentSubmit')
    // setCashPaymentDetails(details);
    // handleCheckout();
    handleClosePayment();
  };

  // Handle when an order is found from order number search
  const handleOrderFound = (orderData) => {
    setLoadedOrderData(orderData);
    // Set cart items based on the retrieved order
    if (orderData.items && orderData.items.length > 0) {
      const transformedItems = orderData.items.map(item => {
        const quantity = item.amount || 1;
        const unitPrice = (item.price / 100) / quantity;
        return {
          id: item._id || Date.now().toString(),
          menuItemId: item.menuItemId || "",
          menuName: item.name 
            ? (item.name.includes('-') 
                ? item.name.split('-').slice(1).join('-').trim() 
                : item.name.trim()
              ) 
            : "",
          quantity,
          price: unitPrice,
          totalPrice: unitPrice * quantity,
          selectedOptions: item.options || [],
          menuTags: item.menuTags || [],
        };
      });
    
      setCartItems(transformedItems);
    
      if (orderData.discountType || orderData.discount) {
        setDiscountDetails({
          discount: orderData.discount || 0,
          discountType: orderData.discountType || 'None',
          discountValue: orderData.discountAmount || '',
          voucherCode: orderData.voucherCode || '',
          voucherDesc: orderData.voucherDesc || '',
          finalTotal: orderData.total || orderData.totalAmount || 0,
        });
      }
    
      if (orderData.salesType) {
        changeOrderType(orderData.salesType);
      }
    
      if (orderData.tentNumber) {
        setTableNumber(orderData.tentNumber);
      }

      if (orderData.receiptNumber) {
        setOrderReceiptNumber(orderData.receiptNumber); 
      }

      if (orderData.orderName) {
        setOrderOrderNumber(orderData.orderName); 
      }

      if (orderData.totalAmount) {
        setTotalAmount(orderData.totalAmount);
      }
    
      setCurrentStage(STAGES.CASH_PAYMENT);
    } else {
      // No items
      setSnackbarMessage(translate("No items found in this order", {
        en: "No items found in this order",
        zh: "此订单中没有找到商品",
        th: "ไม่พบรายการในคำสั่งซื้อนี้",
        tl: "Walang item na nahanap sa order na ito"
      }));
      setSnackbarOpen(true);
      setCurrentStage(STAGES.DINING_LOCATION);
    }
  };
  
  // Handle going back from order number screen
  const handleBackFromOrderNumber = () => {
    setCurrentStage(STAGES.DINING_LOCATION);
  };
  
  // Handle back from cash payment to cart review
  const handleBackToCartReview = () => {
    setCurrentStage(STAGES.CART_REVIEW);
  };

  // Handle adding item to cart
  const handleAddToCart = (item, options = [], quantity = 1, chiliLevelData = null) => {
    const basePrice = item.basePrice / 100;
  
    const newItem = {
      ...item,
      id: Date.now().toString(),
      quantity,
      selectedOptions: options,
      totalPrice: calculateItemPrice(basePrice, options) * quantity,
      chiliLevelData, // <-- Store chili level here
    };
  
    setCartItems([...cartItems, newItem]);
  };

  // Calculate item price with selected options
  const calculateItemPrice = (basePrice, selectedOptions) => {
    let price = parseFloat(basePrice);
    
    selectedOptions.forEach(option => {
      // Ensure option price is normalized the same way as basePrice
      const optionPrice = option.price > 100 ? option.price / 100 : option.price;
      price += parseFloat(optionPrice || 0);
    });
    
    // Return with 2 decimal precision to avoid floating point issues
    return parseFloat(price.toFixed(2));
  };

  // Handle removing item from cart
  const handleRemoveFromCart = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  // Handle updating item quantity in cart
  const handleUpdateQuantity = (itemId, newQuantity) => {
    setCartItems(prevItems => {
      if (newQuantity <= 0) {
        return prevItems.filter(item => item.id !== itemId);       // delete line item
      }
  
      return prevItems.map(item => {
        if (item.id !== itemId) return item;
  
        const unit = item.totalPrice / item.quantity;              // current unit gross
        return {
          ...item,
          quantity: newQuantity,
          totalPrice: unit * newQuantity,
        };
      });
    });
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  // Handle table number selection (legacy)
  const handleTableSelect = (table) => {
    setTableNumber(table);
  };
  
  // Handle order type change (legacy)
  const handleOrderTypeChange = (type) => {
    changeOrderType(type);
  };

  // Handle opening payment dialog
  const handleCheckout = () => {
    // If table number is not set, trigger table number entry
    if (!tableNumber) {
      setSnackbarMessage(translate("Please select your table number before checkout", {
        en: "Please select your table number before checkout",
        zh: "结账前请选择桌号",
        th: "กรุณาเลือกหมายเลขโต๊ะก่อนชำระเงิน",
        tl: "Mangyaring pumili ng numero ng mesa bago mag-checkout"
      }));
      setSnackbarOpen(true);
      setCurrentStage(STAGES.TABLE_NUMBER);
      return;
    }
    
    const subtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const total = subtotal - (discountDetails.discount || 0);
    
    setDiscountDetails(prev => ({
      ...prev,
      // finalTotal: total
    }));
    
    setCurrentStage(STAGES.PAYMENT);
    setPaymentDialogOpen(true);
  };

  // Handle closing payment dialog
  const handleClosePayment = () => {
    setError(null)
    setCartItems([])
    setTableNumber("")
    setDiscountDetails({
      discount: 0,
      discountType: "None",
      discountValue: "",
      voucherCode: "",
      voucherDesc: "",  
    })
    setDiscountDetails2([]);
    setSettingsAnchorEl(null)
    setLanguageMenuAnchorEl(null)
    setPaymentMethodMenuAnchorEl(null)
    setPaymentDialogOpen(false);

    const currentUrl = location.pathname;
    // alert(currentUrl)
    const kioskUrl = currentUrl.replace("/order", "");
    navigate(kioskUrl);
  };

  // Handle opening settings menu
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // Handle closing settings menu
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Handle opening language menu
  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchorEl(event.currentTarget);
    setSettingsAnchorEl(null);
  };

  // Handle closing language menu
  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchorEl(null);
  };

  const handleBackToWelcome = () => {
    const currentUrl = location.pathname;
    // alert(currentUrl)
    const kioskUrl = currentUrl.replace("/order", "");
    navigate(kioskUrl);
  };

  // Handle selecting a language
  const handleLanguageSelect = (langCode) => {
    changeLanguage(langCode);
    setLanguageMenuAnchorEl(null);
  };

  // Handle opening payment method menu
  const handlePaymentMethodMenuOpen = (event) => {
    setPaymentMethodMenuAnchorEl(event.currentTarget);
    setSettingsAnchorEl(null);
  };

  // Handle closing payment method menu
  const handlePaymentMethodMenuClose = () => {
    setPaymentMethodMenuAnchorEl(null);
  };

  // Handle selecting a payment method
  const handlePaymentMethodSelect = (method) => {
    changePaymentMethod(method);
    setPaymentMethodMenuAnchorEl(null);
  };

  const handleClearCart = () => {
    setCartItems([]); 
  };

  const handleCancel = () => {
    setPaymentDialogOpen(false);
  };

  const pushExtraDiscount = (payload) => {
    if (Array.isArray(payload)) {
      setDiscountDetails2(payload);        
    } else if (payload) {
      setDiscountDetails2((prev) => [...prev, payload]);
    }
  };

  // Filter menu items by selected category
  const filteredMenuItems = menuData.availableMenu.filter(
    item => item.categoryName === selectedCategory
  );

  if (loading) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h5">
            {translate("Loading menu...", {
              en: "Loading menu...",
              zh: "正在加载菜单...",
              th: "กำลังโหลดเมนู...",
              tl: "Naglo-load ng menu..."
            })}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h5" color="error">
            {translate(error, {
              en: "Failed to load menu data. Please try again later.",
              zh: "加载菜单数据失败。请稍后再试。",
              th: "ไม่สามารถโหลดข้อมูลเมนูได้ โปรดลองอีกครั้งในภายหลัง",
              tl: "Hindi nagawang mag-load ng data ng menu. Pakisubukang muli mamaya."
            })}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Determine what to render based on the current stage
  const renderContent = () => {
    if (loading) {
      return (
        <Container>
          <Box sx={{ textAlign: "center", my: 4 }}>
            <Typography variant="h5">
              {translate("Loading menu...", {
                en: "Loading menu...",
                zh: "正在加载菜单...",
                th: "กำลังโหลดเมนู...",
                tl: "Naglo-load ng menu..."
              })}
            </Typography>
          </Box>
        </Container>
      );
    }
  
    if (error) {
      return (
        <Container>
          <Box sx={{ textAlign: "center", my: 4 }}>
            <Typography variant="h5" color="error">
              {translate(error, {
                en: "Failed to load menu data. Please try again later.",
                zh: "加载菜单数据失败。请稍后再试。",
                th: "ไม่สามารถโหลดข้อมูลเมนูได้ โปรดลองอีกครั้งในภายหลัง",
                tl: "Hindi nagawang mag-load ng data ng menu. Pakisubukang muli mamaya."
              })}
            </Typography>
          </Box>
        </Container>
      );
    }
    
    switch(currentStage) {
      case STAGES.DINING_LOCATION:
        return (
          <Box sx={{ position: 'relative', height: '100%' }}>
            {/* No back button on the first screen */}
            <DiningLocation onLocationSelect={handleDiningLocationSelect} onBack={handleBackToWelcome} posId={id}/>
          </Box>
        );

      case STAGES.DELIVERY_SELECT:
          return (
            <DeliverySelect
              posId={id}
              onBack={() => setCurrentStage(STAGES.DINING_LOCATION)}
              onSelect={(deliveryName) => {
                setSelectedDelivery(deliveryName);            
                updateSelectedDelivery(deliveryName);          
                setCurrentStage(STAGES.TABLE_NUMBER);
              }}
            />
          );
        
      case STAGES.TABLE_NUMBER:
        return (
          <Box sx={{ position: 'relative', height: '100%' }}>
            {/* Back button to return to Dining Location */}
            <IconButton 
              sx={{ 
                position: 'absolute', 
                top: 16, 
                left: 16, 
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                boxShadow: 2,
                '&:hover': { backgroundColor: theme.palette.action.hover }
              }}
              onClick={() => setCurrentStage(STAGES.DINING_LOCATION)}
              aria-label="back"
            >
              <ArrowBackIcon />
            </IconButton>
            <TableNumber onTableSubmit={handleTableNumberSubmit} onBack={handleBackToDiningLocation} />
          </Box>
        );
        
      case STAGES.ORDER_NUMBER:
        return (
          <OrderNumber 
            onBack={handleBackFromOrderNumber}
            onOrderFound={handleOrderFound}
          />
        );
        
      case STAGES.CART_REVIEW:
        return (
          <CartReview 
            cartItems={cartItems} 
            orderType={orderType}
            onUpdateQuantity={handleUpdateQuantity} 
            onRemoveItem={handleRemoveFromCart}
            onBack={handleBackToMenu}
            onProceedToPayment={handleProceedToPayment}
            discountDetails={discountDetails}
            discountDetails2={discountDetails2}
            pushExtraDiscount={pushExtraDiscount}
            posId={id}
          />
        );
        
      case STAGES.CASH_PAYMENT:
        return (
          <CashPayment 
            total={discountDetails.finalTotal || 
              cartItems.reduce((sum, item) => sum + item.totalPrice, 0) - discountDetails.discount}
            totalAmount={discountDetails.finalTotal}
            onBack={handleBackToCartReview}
            onSubmit={handleCashPaymentSubmit}
            posId={id}
            orderType={orderType}
            discountDetails={discountDetails}
            discountDetails2={discountDetails2}
            pushExtraDiscount={pushExtraDiscount}
            cartItems={cartItems}
            existingReceiptNumber={orderReceiptNumber}
            orderNumber={orderOrderNumber}
            
          />
        );
        
      case STAGES.ORDER_MENU:
      default:
        return renderOrderingScreen();
    }
  };
  
  // Render the ordering screen with categories, menu items, and cart
  const renderOrderingScreen = () => {
    return (
      <>
        {/* Header with back button and logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid', borderColor: 'divider', position: 'relative' }}>
          {/* Back button */}
          <IconButton 
            sx={{ 
              position: 'absolute',
              left: 16,
              zIndex: 10,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': { backgroundColor: theme.palette.action.hover }
            }}
            onClick={() => setCurrentStage(STAGES.TABLE_NUMBER)}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          
          {/* Logo at the top */}
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <img src="/assets/images/logo.png" alt="Sparkle Pay" style={{ height: '60px' }} />
          </Box>
        </Box>
      
        <Container maxWidth="xl" sx={{ py: 2, position: 'relative', height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>          
          <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {/* Categories - 30% of screen */}
            <Grid item xs={12} md={4} lg={3} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={3} sx={{ p: 2, mb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto',
                  overflowY: 'scroll', // Always show vertical scrollbar
                  // Make scrollbar always visible and larger for better tablet interaction
                  '&::-webkit-scrollbar': {
                    width: '16px', // Larger scrollbar width
                    height: '16px',
                    display: 'block', // Always show
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    margin: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(25,118,210,0.5)', // Using primary color (blue)
                    borderRadius: '8px',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box',
                    '&:hover': {
                      backgroundColor: 'rgba(25,118,210,0.7)',
                    }
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: 'auto', // Wider scrollbar for Firefox
                  scrollbarColor: 'rgba(25,118,210,0.5) rgba(0,0,0,0.08)', // Primary color
                }}>
                <MenuCategories 
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategorySelect={handleCategorySelect}
                  translate={translate}
                />
                </Box>
              </Paper>
            </Grid>
            
            {/* Menu Items - 70% of screen */}
            <Grid item xs={12} md={8} lg={9} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto', 
                  overflowY: 'scroll', // Always show vertical scrollbar
                  pb: 2,
                  // Make scrollbar always visible and larger for better tablet interaction
                  '&::-webkit-scrollbar': {
                    width: '16px', // Larger scrollbar width
                    height: '16px',
                    display: 'block', // Always show
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    margin: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(25,118,210,0.5)', // Using primary color (blue)
                    borderRadius: '8px',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box',
                    '&:hover': {
                      backgroundColor: 'rgba(25,118,210,0.7)',
                    }
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: 'auto', // Wider scrollbar for Firefox
                  scrollbarColor: 'rgba(25,118,210,0.5) rgba(0,0,0,0.08)', // Primary color
                }}>
                  <MenuItems 
                    menuItems={menuData.availableMenu.filter(item => item.categoryName === selectedCategory)}
                    onAddToCart={handleAddToCart}
                    translate={translate}
                    language={language}
                    formatPrice={formatPrice}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Bottom Actions Bar */}
          <Box 
            sx={{ 
              position: 'fixed', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              py: 2, 
              px: 3, 
              bgcolor: 'background.paper', 
              borderTop: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 100,
            }}
          >
            {/* Start Over Button */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<RestartAltIcon />}
              onClick={handleStartOver}
            >
              {translate("Start Over", {
                en: "Start Over",
                zh: "重新开始",
                th: "เริ่มใหม่",
                tl: "Magsimula Muli"
              })}
            </Button>
            
            {/* Cart Button with Count and Total */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewCart}
              startIcon={
                <Badge badgeContent={cartItems.length} color="error" max={99}>
                  <ShoppingCartIcon />
                </Badge>
              }
              disabled={cartItems.length === 0}
              sx={{ minWidth: 200, maxWidth: 600, width: '100%' }}
            >
              {formatPrice(cartItems.reduce((sum, item) => sum + item.totalPrice, 0))}
            </Button>
          </Box>
        </Container>
      </>
    );
  };

  return (
    <>
      {/* App Bar with settings - only show in ordering screen */}
      {currentStage === STAGES.ORDER_MENU && (
      //   <AppBar position="static" color="primary" elevation={2}>
      //     <Toolbar>
      //       <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
      //         {translate("Restaurant Ordering Kiosk", {
      //           en: "Restaurant Ordering Kiosk",
      //           zh: "餐厅点餐乾",
      //           th: "ตู้สั่งอาหาร",
      //           tl: "Restaurant Ordering Kiosk"
      //         })}
      //       </Typography>
            
      //       {/* Currently selected language */}
      //       <Typography variant="body1" sx={{ mr: 2 }}>
      //         {availableLanguages[language]?.flag}
      //       </Typography>
            
      //       {/* Settings button */}
      //       <IconButton 
      //         color="inherit" 
      //         onClick={handleSettingsClick}
      //         aria-controls="settings-menu"
      //         aria-haspopup="true"
      //       >
      //         <SettingsIcon />
      //       </IconButton>
            
      //       {/* Settings menu */}
      //       <Menu
      //         id="settings-menu"
      //         anchorEl={settingsAnchorEl}
      //         open={Boolean(settingsAnchorEl)}
      //         onClose={handleSettingsClose}
      //         anchorOrigin={{
      //           vertical: 'bottom',
      //           horizontal: 'right',
      //         }}
      //         transformOrigin={{
      //           vertical: 'top',
      //           horizontal: 'right',
      //         }}
      //       >
      //         <MenuItem onClick={toggleDarkMode}>
      //           {darkMode ? <Brightness7Icon sx={{ mr: 1 }} /> : <Brightness4Icon sx={{ mr: 1 }} />}
      //           {darkMode ? translate("Light Mode", {
      //             en: "Light Mode",
      //             zh: "亮色模式",
      //             th: "โหมดสว่าง",
      //             tl: "Light Mode"
      //           }) : translate("Dark Mode", {
      //             en: "Dark Mode",
      //             zh: "暗色模式",
      //             th: "โหมดมืด",
      //             tl: "Dark Mode"
      //           })}
      //         </MenuItem>
      //         <MenuItem onClick={handleLanguageMenuOpen}>
      //           <LanguageIcon sx={{ mr: 1 }} />
      //           {translate("Language", {
      //             en: "Language",
      //             zh: "语言",
      //             th: "ภาษา",
      //             tl: "Wika"
      //           })}
      //         </MenuItem>
      //         <MenuItem onClick={handlePaymentMethodMenuOpen}>
      //           {translate("Payment Method", {
      //             en: "Payment Method",
      //             zh: "支付方式",
      //             th: "วิธีการชำระเงิน",
      //             tl: "Paraan ng Pagbabayad"
      //           })}
      //         </MenuItem>
      //       </Menu>
            
      //       {/* Language selection menu */}
      //       <Menu
      //         id="language-menu"
      //         anchorEl={languageMenuAnchorEl}
      //         open={Boolean(languageMenuAnchorEl)}
      //         onClose={handleLanguageMenuClose}
      //       anchorOrigin={{
      //         vertical: 'bottom',
      //         horizontal: 'right',
      //       }}
      //       transformOrigin={{
      //         vertical: 'top',
      //         horizontal: 'right',
      //       }}
      //     >
      //       {Object.entries(availableLanguages).map(([code, lang]) => (
      //         <MenuItem 
      //           key={code} 
      //           onClick={() => handleLanguageSelect(code)}
      //           selected={language === code}
      //         >
      //           <Box sx={{ display: 'flex', alignItems: 'center' }}>
      //             <Typography sx={{ mr: 1 }}>{lang.flag}</Typography>
      //             <Typography>{lang.name}</Typography>
      //           </Box>
      //         </MenuItem>
      //       ))}
      //     </Menu>
          
      //     {/* Payment method selection menu */}
      //     <Menu
      //       id="payment-method-menu"
      //       anchorEl={paymentMethodMenuAnchorEl}
      //       open={Boolean(paymentMethodMenuAnchorEl)}
      //       onClose={handlePaymentMethodMenuClose}
      //       anchorOrigin={{
      //         vertical: 'bottom',
      //         horizontal: 'right',
      //       }}
      //       transformOrigin={{
      //         vertical: 'top',
      //         horizontal: 'right',
      //       }}
      //     >
      //       <MenuItem 
      //         onClick={() => handlePaymentMethodSelect('cash')}
      //         selected={paymentMethod === 'cash'}
      //       >
      //         {translate("Cash", {
      //           en: "Cash",
      //           zh: "现金",
      //           th: "เงินสด",
      //           tl: "Cash"
      //         })}
      //       </MenuItem>
      //       <MenuItem 
      //         onClick={() => handlePaymentMethodSelect('credit_card')}
      //         selected={paymentMethod === 'credit_card'}
      //       >
      //         {translate("Credit Card", {
      //           en: "Credit Card",
      //           zh: "信用卡",
      //           th: "บัตรเครดิต",
      //           tl: "Credit Card"
      //         })}
      //       </MenuItem>
      //       <MenuItem 
      //         onClick={() => handlePaymentMethodSelect('gcash')}
      //         selected={paymentMethod === 'gcash'}
      //       >
      //         GCash
      //       </MenuItem>
      //     </Menu>
      //   </Toolbar>
      // </AppBar>
      <></>
      )}
      
      {/* Main Content */}
      {renderContent()}
      
      {/* Payment Dialog */}
      <PaymentDialog 
        open={paymentDialogOpen} 
        onClose={handleClosePayment}
        onCancel={handleCancel}
        cartItems={cartItems}
        tableNumber={tableNumber}
        posId={id}
        discount={discountDetails.discount}
        discountType={discountDetails.discountType}
        discountValue={discountDetails.discountValue}
        pushExtraDiscount={pushExtraDiscount}
        discountDetails2={discountDetails2}
        voucherCode={discountDetails.voucherCode}
        voucherDesc={discountDetails.voucherDesc}
        finalTotal={discountDetails.finalTotal}
        paymentMethod={paymentMethod}
        translate={translate}
        onClearCart={handleClearCart}
        customerId={customerInfo.customerId}
        orderId={customerInfo.orderId}
        cashPaymentDetails={cashPaymentDetails}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="warning"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

// Export the wrapped component
const KioskPage = () => <KioskPageWrapper />;


export default KioskPage;